import axios from 'axios';
import {
  getAccessToken, clearTokens, refreshAccessToken, isPublicPath,
} from './authClient';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const api = axios.create({ baseURL: API_BASE });

// Attach JWT token to every request
api.interceptors.request.use((config) => {
  const token = getAccessToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// The refresh call itself must not go through this instance's interceptors,
// or a failing refresh would recurse.
const requestRefresh = (refresh) =>
  axios.post(`${API_BASE}/api/auth/token/refresh/`, { refresh });

const endSession = () => {
  clearTokens();
  // Don't yank a visitor off a page they're allowed to be on — that turns an
  // expired token on the home page into a jarring redirect to sign-in.
  if (!isPublicPath(window.location.pathname)) {
    window.location.href = '/signin';
  }
};

// Auto-refresh on 401
api.interceptors.response.use(
  (res) => res,
  async (error) => {
    const orig = error.config;

    // Only a 401 is a token problem. A network error or 5xx leaves the session
    // alone — the user stays signed in and the caller handles the failure.
    if (error.response?.status !== 401 || !orig || orig._retry) {
      return Promise.reject(error);
    }

    // A 401 from the refresh endpoint itself means the refresh token is dead.
    if (orig.url?.includes('/api/auth/token/refresh/')) {
      endSession();
      return Promise.reject(error);
    }

    orig._retry = true;
    try {
      // Concurrent 401s share one refresh; see api/authClient.js
      const access = await refreshAccessToken(requestRefresh);
      orig.headers.Authorization = `Bearer ${access}`;
      return api(orig);
    } catch (refreshError) {
      // Refresh genuinely rejected => signed out. Refresh failed because the
      // network/server is down => keep the session and surface the error.
      if (refreshError?.response || refreshError?.message === 'no_refresh_token') {
        endSession();
      }
      return Promise.reject(error);
    }
  }
);

// AUTH
export const login = (credentials) => api.post('/api/auth/token/', credentials);
export const refreshToken = (refresh) => api.post('/api/auth/token/refresh/', { refresh });
export const registerUser = (data) => api.post('/api/v1/users/', data);
export const getMe = () => api.get('/api/v1/users/me/');
export const checkAvailability = (params) => api.get('/api/v1/users/check/', { params });

// PRODUCTS
export const getProducts = (params) => api.get('/api/v1/products/', { params });
export const getProduct = (slug) => api.get(`/api/v1/products/${slug}/`);
export const createProduct = (data) => api.post('/api/v1/products/', data);
export const updateProduct = (slug, data) => api.patch(`/api/v1/products/${slug}/`, data);
export const deleteProduct = (slug) => api.delete(`/api/v1/products/${slug}/`);

// CATEGORIES
export const getCategories = (params) => api.get('/api/v1/categories/', { params });
export const createCategory = (data) => api.post('/api/v1/categories/', data);
export const updateCategory = (id, data) => api.patch(`/api/v1/categories/${id}/`, data);
export const deleteCategory = (id) => api.delete(`/api/v1/categories/${id}/`);

// PRODUCT IMAGES
export const getProductImages = (params) => api.get('/api/v1/product-images/', { params });
export const createProductImage = (data) => api.post('/api/v1/product-images/', data, { headers: { 'Content-Type': 'multipart/form-data' } });
export const deleteProductImage = (id) => api.delete(`/api/v1/product-images/${id}/`);

// CART
export const getCarts = () => api.get('/api/v1/carts/');
export const createCart = () => api.post('/api/v1/carts/');
export const updateCart = (id, data) => api.patch(`/api/v1/carts/${id}/`, data);
export const getCartItems = () => api.get('/api/v1/cart-items/');
export const addCartItem = (data) => api.post('/api/v1/cart-items/', data);
export const updateCartItem = (id, data) => api.patch(`/api/v1/cart-items/${id}/`, data);
export const deleteCartItem = (id) => api.delete(`/api/v1/cart-items/${id}/`);

// ORDERS
export const getOrders = (params) => api.get('/api/v1/orders/', { params });
export const getOrder = (id) => api.get(`/api/v1/orders/${id}/`);
export const createOrder = (data) => api.post('/api/v1/orders/', data);
export const updateOrder = (id, data) => api.patch(`/api/v1/orders/${id}/`, data);
export const deleteOrder = (id) => api.delete(`/api/v1/orders/${id}/`);

// PAYMENTS
export const getPayments = () => api.get('/api/v1/payments/');
export const createPayment = (data) => api.post('/api/v1/payments/', data);
export const updatePayment = (id, data) => api.patch(`/api/v1/payments/${id}/`, data);

// USERS (admin)
export const getUsers = (params) => api.get('/api/v1/users/', { params });
export const getUser = (id) => api.get(`/api/v1/users/${id}/`);
export const updateUser = (id, data) => api.patch(`/api/v1/users/${id}/`, data);
export const deleteUser = (id) => api.delete(`/api/v1/users/${id}/`);

// PAYMENT GATEWAYS (Khalti / Fonepay)
export const getPaymentMethods = () => api.get('/api/v1/payments/methods/');
export const initiatePayment = (data) => api.post('/api/v1/payments/initiate/', data);
export const verifyPayment = (data) => api.post('/api/v1/payments/verify/', data);

// PASSWORD
export const changePassword = (data) => api.post('/api/v1/users/change-password/', data);
export const adminSetPassword = (id, data) => api.post(`/api/v1/users/${id}/set-password/`, data);

// ADDRESSES
export const getAddresses = () => api.get('/api/v1/addresses/');
export const createAddress = (data) => api.post('/api/v1/addresses/', data);
export const updateAddress = (id, data) => api.patch(`/api/v1/addresses/${id}/`, data);
export const deleteAddress = (id) => api.delete(`/api/v1/addresses/${id}/`);

// CHECKOUTS
export const getCheckouts = () => api.get('/api/v1/checkouts/');
export const createCheckout = (data) => api.post('/api/v1/checkouts/', data);

export default api;

// BLOG
export const getBlogArticles = (params) => api.get('/api/v1/blog/articles/', { params });
export const generateBlogArticles = (count = 3) => api.post('/api/v1/blog/articles/generate/', { count });
export const getBlogArticle = (slug) => api.get(`/api/v1/blog/articles/${slug}/`);
export const createBlogArticle = (data) => api.post('/api/v1/blog/articles/', data);
export const updateBlogArticle = (slug, data) => api.patch(`/api/v1/blog/articles/${slug}/`, data);
export const deleteBlogArticle = (slug) => api.delete(`/api/v1/blog/articles/${slug}/`);

export const getBlogCategories = (params) => api.get('/api/v1/blog/categories/', { params });
export const createBlogCategory = (data) => api.post('/api/v1/blog/categories/', data);
export const deleteBlogCategory = (slug) => api.delete(`/api/v1/blog/categories/${slug}/`);

export const getBlogTags = (params) => api.get('/api/v1/blog/tags/', { params });
export const createBlogTag = (data) => api.post('/api/v1/blog/tags/', data);
export const deleteBlogTag = (slug) => api.delete(`/api/v1/blog/tags/${slug}/`);

export const uploadBlogImage = (data) => api.post('/api/v1/blog/images/', data, { headers: { 'Content-Type': 'multipart/form-data' } });
export const deleteBlogImage = (id) => api.delete(`/api/v1/blog/images/${id}/`);
export const updateBlogArticleFeaturedImage = (slug, file) => {
  const fd = new FormData();
  fd.append('featured_image', file);
  return api.patch(`/api/v1/blog/articles/${slug}/`, fd, { 
    headers: { 'Content-Type': 'multipart/form-data' } 
  });
};

// SECOND HAND MARKETPLACE
export const getSecondHandItems = (params) => api.get('/api/v1/second-hand/', { params });
export const getMySecondHandItems = (params) => api.get('/api/v1/second-hand/mine/', { params });
export const getSecondHandItem = (slug) => api.get(`/api/v1/second-hand/${slug}/`);
export const createSecondHandItem = (data) => api.post('/api/v1/second-hand/', data);
export const updateSecondHandItem = (slug, data) => api.patch(`/api/v1/second-hand/${slug}/`, data);
export const deleteSecondHandItem = (slug) => api.delete(`/api/v1/second-hand/${slug}/`);
export const approveSecondHandItem = (slug) => api.post(`/api/v1/second-hand/${slug}/approve/`);
export const rejectSecondHandItem = (slug, reason) => api.post(`/api/v1/second-hand/${slug}/reject/`, { reason });
export const getSecondHandPendingCount = () => api.get('/api/v1/second-hand/pending-count/');

export const getSecondHandImages = (params) => api.get('/api/v1/second-hand-images/', { params });
export const uploadSecondHandImage = (data) => api.post('/api/v1/second-hand-images/', data, { headers: { 'Content-Type': 'multipart/form-data' } });
export const deleteSecondHandImage = (id) => api.delete(`/api/v1/second-hand-images/${id}/`);
